import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  VisibilityOff as DiscreetIcon,
  Savings as DepositIcon,
  Schedule as ScheduleIcon,
  Bolt as FastIcon,
  CheckCircle as CheckIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SectionContainer from '../components/SectionContainer';
import { useLanguage } from '../i18n/LanguageContext';

const Students = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const whyItems = [
    { icon: <DiscreetIcon sx={{ fontSize: 40, color: 'secondary.main' }} />, key: 'discreet' },
    { icon: <DepositIcon sx={{ fontSize: 40, color: 'secondary.main' }} />, key: 'deposit' },
    { icon: <ScheduleIcon sx={{ fontSize: 40, color: 'secondary.main' }} />, key: 'schedule' },
    { icon: <FastIcon sx={{ fontSize: 40, color: 'secondary.main' }} />, key: 'fast' },
  ];

  const fixes = t('students.fixes') || [];

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a365d 0%, #2d5a87 100%)',
          color: 'white',
          py: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h1" gutterBottom sx={{ fontWeight: 700 }}>
                {t('students.heroTitle')}
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontWeight: 400 }}>
                {t('students.heroSubtitle')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={() => navigate('/contact')}
                  sx={{ px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 600 }}
                >
                  {t('students.heroButton')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="tel:555-555-5555"
                  startIcon={<PhoneIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'secondary.main',
                      backgroundColor: 'rgba(212, 175, 55, 0.1)',
                    },
                  }}
                >
                  {t('students.heroCall')}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="/images/student-housing.jpg"
                alt={t('students.heroTitle')}
                sx={{ width: '100%', height: 'auto', borderRadius: 2, boxShadow: 4 }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why students choose Chato */}
      <SectionContainer>
        <Typography variant="h2" textAlign="center" gutterBottom sx={{ mb: 6 }}>
          {t('students.whyTitle')}
        </Typography>
        <Grid container spacing={4}>
          {whyItems.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.key}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent>
                  <Box sx={{ mb: 2 }}>{item.icon}</Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {t(`students.why.${item.key}.title`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(`students.why.${item.key}.description`)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </SectionContainer>

      {/* Common fixes */}
      <SectionContainer backgroundColor="grey.50">
        <Typography variant="h2" textAlign="center" gutterBottom sx={{ mb: 2 }}>
          {t('students.fixesTitle')}
        </Typography>
        <Typography
          variant="h6"
          textAlign="center"
          color="text.secondary"
          sx={{ mb: 5, maxWidth: 700, mx: 'auto', fontWeight: 400 }}
        >
          {t('students.fixesSubtitle')}
        </Typography>
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Grid container>
            {(Array.isArray(fixes) ? fixes : []).map((fix, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckIcon color="secondary" />
                    </ListItemIcon>
                    <ListItemText primary={fix} />
                  </ListItem>
                </List>
              </Grid>
            ))}
          </Grid>
          <Typography
            variant="body2"
            textAlign="center"
            color="text.secondary"
            sx={{ mt: 3, fontStyle: 'italic' }}
          >
            {t('students.areasNote')}
          </Typography>
        </Box>
      </SectionContainer>

      {/* CTA */}
      <SectionContainer backgroundColor="primary.main" sx={{ color: 'white' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
            {t('students.ctaTitle')}
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            {t('students.ctaSubtitle')}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => navigate('/contact')}
            sx={{ px: 4, py: 2, fontSize: '1.1rem' }}
          >
            {t('students.ctaButton')}
          </Button>
        </Box>
      </SectionContainer>
    </Box>
  );
};

export default Students;
